import { ReactComponent as Logo } from "../../logo.svg";
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from "../../context/userContext";
import { flushSync } from "react-dom";
import "./sideNavbar.css";

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import InventoryIcon from '@mui/icons-material/Inventory';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';

const UserRole = {
    SUPER_ADMIN: 'Super Admin',
    ADMIN: 'Admin',
    ACCOUNTANT: 'Accountant',
    LEADER: 'Leader',
    SALES_AGENT: 'Sales Agent',
    IN_HOUSE_AGENT: 'In-house Agent'
};

const NAV_ITEMS = [
    {
        label: 'Dashboard',
        path: '/dashboard',
        icon: DashboardIcon,
        allowedRoles: [UserRole.SALES_AGENT, UserRole.IN_HOUSE_AGENT]
    },
    {
        label: 'Sales',
        path: '/sales',
        icon: ShoppingCartIcon,
        allowedRoles: [UserRole.SALES_AGENT, UserRole.IN_HOUSE_AGENT],
        children: [
            {
                label: 'All Sales',
                path: '/sales'
            },
            {
                label: 'Create Sale',
                path: '/sales/create'
            }
        ]
    },
    {
        label: 'Commission',
        path: '/commission',
        icon: MonetizationOnIcon,
        allowedRoles: [UserRole.SALES_AGENT, UserRole.IN_HOUSE_AGENT],
        children: [
            {
                label: 'Personal (PSC)',
                path: '/commissions/psc'
            },
            {
                label: 'Team (TOC)',
                path: '/commissions/toc',
                allowedRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.ACCOUNTANT, UserRole.LEADER]
            },
            {
                label: 'Downline (DOC)',
                path: '/commissions/doc'
            }
        ]
    },
    {
        label: 'All Claims',
        path: '/claims',
        icon: AssignmentIcon,
        allowedRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.ACCOUNTANT]
    },
    {
        label: 'Customers',
        path: '/customers',
        icon: PeopleIcon,
    },
    {
        label: 'Agents',
        path: '/agents',
        icon: VerifiedUserIcon,
        allowedRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN]
    },
    {
        label: 'Reports',
        path: '/reports',
        icon: AssessmentIcon,
        children: [
            {
                label: 'Commission Report',
                path: '/reports/commission'
            },
            {
                label: 'Customer Report',
                path: '/reports/customer'
            }
        ]
    },
    {
        label: 'Products',
        path: '/products',
        icon: InventoryIcon,
        allowedRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN]
    },
    {
        label: 'Accounting',
        path: '/accounting',
        icon: MonetizationOnIcon,
        children: [
            {
                label: 'Overview',
                path: "/accounting/overview"
            },
            {
                label: 'Webbey Commissions',
                path: '/accounting/webbeyCommissions'
            },
            {
                label: 'All Sales Commissions',
                path: '/accounting/salesCommissions'
            }
        ],
        allowedRoles: [UserRole.SUPER_ADMIN, UserRole.ACCOUNTANT]
    },
    {
        label: 'Admin',
        path: '/admin',
        icon: PeopleIcon,
        children: [
            {
                label: 'Sales',
                path: "/admin/sales"
            },
            {
                label: 'Groups',
                path: '/admin/group'
            },
            {
                label: 'Agents',
                path: '/admin/agents'
            }
        ],
        allowedRoles: [UserRole.SUPER_ADMIN]
    },
    {
        label: 'Settings',
        path: '/settings',
        icon: SettingsIcon,
        children: [
            {
                label: 'Profile',
                path: '/user/profile'
            },
            {
                label: 'Permissions',
                path: '/settings/permissions',
                allowedRoles: [UserRole.SUPER_ADMIN]
            }
        ]
    }
];

export const SideNavbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, setUser } = useUser();
    // const [openMenus, setOpenMenus] = useState(['Sales', 'Commission', 'Settings']); //expand the menu by default
    const [openMenus, setOpenMenus] = useState([]);

    // Get user roles from context
    const userRoles = Array.isArray(user?.roles) ? user.roles : [];
    const username = user?.username;

    // Unified state: isOpen controls visibility for both Mobile and Desktop
    const [isOpen, setIsOpen] = useState(window.innerWidth >= 768);

    const toggleMenu = (label) => {
        if (openMenus.includes(label)) {
            setOpenMenus(openMenus.filter(item => item !== label));
        } else {
            setOpenMenus([...openMenus, label]);
        }
    };

    const handleLogout = () => {
        flushSync(() => {
            setUser(null);
            localStorage.clear();
        });
        navigate("/");
    };

    // Handle resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                if (!isOpen) setIsOpen(true);
            } else {
                setIsOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen]);

    const toggleSidebar = () => setIsOpen(!isOpen);

    // Helper to check role access
    const hasAccess = (allowedRoles) => {
        if (!allowedRoles) return true;
        return allowedRoles.some(role => userRoles.includes(role));
    };

    return (
        <>
            {/* Hamburger Button */}
            {!isOpen && (
                <button className="hamburger-btn" onClick={toggleSidebar}>
                    <MenuIcon />
                </button>
            )}

            {/* Backdrop - Only for Mobile Overlay */}
            {isOpen && window.innerWidth < 768 && (
                <div className="sidebar-backdrop" onClick={() => setIsOpen(false)} />
            )}

            <div
                className={`modern-sidebar ${isOpen ? 'open' : ''}`}
            >
                <div className="sidebar-header">
                    {/* <div className="logo-box">
                        S
                    </div> */}
                    <img
                        src="/nexwaveLogo.png"
                        alt="nexwave logo"
                        width="40"
                        height="40"
                        className="d-inline-block align-top"
                    />
                    <span className="brand-name">nexwave</span>
                    <button
                        className="sidebar-close-btn"
                        onClick={toggleSidebar}
                        aria-label="Close sidebar"
                    >
                        <ChevronLeftIcon sx={{ fontSize: 20 }} />
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {NAV_ITEMS.map((item) => {
                        if (!hasAccess(item.allowedRoles)) {
                            return null;
                        }

                        const Icon = item.icon || DashboardIcon;

                        return (
                            <div key={item.label}>
                                {item.children ? (
                                    <div>
                                        <button
                                            onClick={() => toggleMenu(item.label)}
                                            className="nav-item-btn"
                                        >
                                            <div className="nav-content">
                                                <Icon sx={{ fontSize: 18 }} />
                                                <span>{item.label}</span>
                                            </div>
                                            {openMenus.includes(item.label) ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
                                        </button>

                                        {openMenus.includes(item.label) && (
                                            <div className="submenu">
                                                {item.children.map(child => {
                                                    if (!hasAccess(child.allowedRoles)) {
                                                        return null;
                                                    }

                                                    return (
                                                        <Link
                                                            key={child.path}
                                                            to={child.path}
                                                            className={`submenu-link ${location.pathname === child.path ? 'active' : ''}`}
                                                        >
                                                            {child.label}
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Link
                                        to={item.path}
                                        className={`nav-item-link ${location.pathname === item.path ? 'active' : ''}`}
                                    >
                                        <Icon sx={{ fontSize: 18 }} />
                                        <span>{item.label}</span>
                                    </Link>
                                )}
                            </div>
                        );
                    })}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-profile">
                        <div className="user-avatar">
                            {username?.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ overflow: 'hidden', flex: 1 }}>
                            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{username}</p>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={userRoles.join(', ')}>{userRoles.join(', ')}</p>
                        </div>
                        <button
                            onClick={handleLogout}
                            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0 }}
                            title="Logout"
                        >
                            <LogoutIcon sx={{ fontSize: 20 }} />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
